import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useFragment } from "react-relay";

import { FormQuestionConfiguration } from "@whoosh/design-system/src/components/controls/EventFormRenderer";

import { mergeEligibleWindowCharges } from "../../../utils/mergeEligibleWindowCharges";
import { initializeEventSignUpProcessAtom } from "../atoms/eventSignUpProcess";
import { EventSignUpProcessSteps, EventSignUpRow } from "../types";
import { useInitializeEventSignUpProcess_event$key } from "./__generated__/useInitializeEventSignUpProcess_event.graphql";

export const useInitializeEventSignUpProcess = ({
  signUpRow,
  eventRef,
  currentMemberUserId,
  isOpen,
  onSaveCallback,
  stepToStartOn,
  isStaff,
  isStaffAdmin,
  eventRegistrationCleanup,
  isWaitlistSignup,
}: {
  signUpRow: EventSignUpRow;
  currentMemberUserId?: string;
  eventRef: useInitializeEventSignUpProcess_event$key;
  isOpen: boolean;
  onSaveCallback: (signUpRow: EventSignUpRow, notifyMembers?: boolean) => void;
  stepToStartOn?: EventSignUpProcessSteps;
  isStaff?: boolean;
  isStaffAdmin?: boolean;
  eventRegistrationCleanup?: boolean;
  isWaitlistSignup?: boolean;
}) => {
  const initializeEventSignUpProcess = useSetAtom(
    initializeEventSignUpProcessAtom
  );
  const hasInitializedForCurrentOpen = useRef(false);

  const isMemberUser = currentMemberUserId !== undefined;

  const event = useFragment(
    useInitializeEventSignUpProcessFragmentString,
    eventRef
  );
  const isGroupEvent =
    event?.maxGuestCount !== 0 ||
    event?.maxMemberCount !== 1 ||
    event?.minMemberCount !== 1 ||
    event?.minGuestCount !== 0;
  const eventHasForm =
    (event.formConfiguration?.formQuestionConfigurations?.count ?? 0) > 0;

  const formQuestionConfigurations: FormQuestionConfiguration[] =
    useMemo(() => {
      return (
        event?.formConfiguration?.formQuestionConfigurations?.edges?.flatMap(
          (f) => {
            return f?.node?.enabled
              ? [
                  {
                    id: f?.node?.id ?? "",
                    prompt: f?.node?.prompt ?? "",
                    description: f?.node?.description ?? null,
                    type: f?.node?.type ?? "TEXT",
                    guestVisibility: f?.node?.guestVisibility ?? "NONE",
                    memberVisibility: f?.node?.memberVisibility ?? "NONE",
                    values: f?.node?.values?.map((v) => v) ?? [],
                    enabled: f?.node?.enabled ?? false,
                    shortname: f?.node?.shortName ?? "",
                    target: f?.node?.target ?? "PLAYER",
                  },
                ]
              : [];
          }
        ) ?? []
      );
    }, [event?.formConfiguration?.formQuestionConfigurations?.edges]);

  /**
   * Charges + their visibility live on the per-window join edge.
   * - Members see charges from the windows they're eligible for
   *   (clientEligibleSignUpWindows); the backend already strips HIDDEN charges.
   * - Staff see charges across all windows (signUpWindows), gated below by
   *   staffControl + their admin role.
   */
  const charges = useMemo(
    () =>
      mergeEligibleWindowCharges(
        isMemberUser
          ? event.eligibleWindowsForCharges
          : event.signUpWindows?.edges?.map((e) => e?.node)
      ),
    [isMemberUser, event.eligibleWindowsForCharges, event.signUpWindows?.edges]
  );

  // A staff user can pick any charge except ADMIN_ONLY ones, which require a
  // department admin. A missing staffControl (legacy charges) stays selectable.
  const staffCanSelectCharge = (charge: (typeof charges)[number]) =>
    !!charge.node.id &&
    (charge.staffControl !== "ADMIN_ONLY" || !!isStaffAdmin);

  /**
   * If we are a member user, only "selectable" charges are selectable. If we
   * are a staff user, selectability is gated by staffControl + admin role.
   */
  const hasMoreThanOneSelectablePrimaryChargeType =
    charges.filter((prct) =>
      isMemberUser
        ? prct.memberControl === "SELECTABLE"
        : staffCanSelectCharge(prct)
    ).length > 1;

  /**
   * Used because sometimes there are only non-selectable charges ie: only 'visible'
   * and we still need the member users to be able to default to those in that case.
   */
  const isMemberAndOnlyNonSelectableChargesExist =
    !hasMoreThanOneSelectablePrimaryChargeType && charges.length > 0;

  const onlyHiddenChargesExist = !charges.find(
    (c) => c.memberControl !== "HIDDEN"
  );
  const eventHasPrimaryCharges =
    (event.primaryRegistrationChargeTypes?.count ?? 0) > 0;

  /**
   * If we are a member user, the first "selectable" one is default. If we are staff, the first
   * one we find at all is default
   */
  const defaultPrimaryChargeId =
    charges.find((prct) =>
      isMemberUser && !isMemberAndOnlyNonSelectableChargesExist
        ? prct.memberControl === "SELECTABLE"
        : isMemberUser
        ? !!prct.node.id
        : staffCanSelectCharge(prct)
    )?.node.id ?? null;

  /**
   * This will be used to pull prices out for ease of calculation
   */
  const primaryChargeIdToPriceMap = useMemo(() => {
    return charges.flatMap((e) => {
      const curPrice = e.node.chargeRuleSet.chargeRules?.edges?.find(
        (cr) => cr?.node?.price != null
      )?.node?.price;
      const curId = e.node.id;
      if (curId && curPrice != null) {
        return [{ id: curId, price: curPrice }];
      }
      return [];
    });
  }, [charges]);

  /**
   * We allow users to select who pays if charges are per person AND if they are a member user - there are at least some visible or selectable charges)
   */
  const eventRequiresPrimaryChargeWhoPaysSection =
    eventHasPrimaryCharges &&
    isGroupEvent &&
    (!isMemberUser || !onlyHiddenChargesExist) &&
    event.primaryChargeType === "PER_PERSON";

  const shouldShowPerRegistrationChargeSelection =
    eventHasPrimaryCharges &&
    isGroupEvent &&
    event.primaryChargeType === "PER_PERSON";

  const clubSupportsPublic =
    !!event.eventSeries?.facility?.club?.supportsPublic;

  useEffect(() => {
    if (!isOpen) {
      hasInitializedForCurrentOpen.current = false;
      return;
    }

    if (!hasInitializedForCurrentOpen.current) {
      hasInitializedForCurrentOpen.current = true;
      let steps = getEventSteps(
        isGroupEvent || !isMemberUser || !!event.memberSignUpProxyAllowed,
        eventHasForm,
        hasMoreThanOneSelectablePrimaryChargeType ||
          shouldShowPerRegistrationChargeSelection,
        eventRequiresPrimaryChargeWhoPaysSection && !clubSupportsPublic
      );

      /**
       * When deep-linking to a specific step we focus the wizard on just that
       * step. A PRIMARY_CHARGE_SELECTION deep-link (e.g. "edit charges") is
       * always a focused, single-step edit, even though the charge selection
       * step is now also part of the normal public sign-up flow.
       */
      if (
        stepToStartOn &&
        (!steps.includes(stepToStartOn) ||
          stepToStartOn === "PRIMARY_CHARGE_SELECTION")
      ) {
        steps = [stepToStartOn];
        if (
          stepToStartOn === "PRIMARY_CHARGE_SELECTION" &&
          eventRequiresPrimaryChargeWhoPaysSection &&
          !clubSupportsPublic
        ) {
          steps.push("PRIMARY_CHARGE_WHO_PAYS");
        }
      }

      if (steps.length === 0) {
        onSaveCallback(signUpRow);
      } else {
        const groupSizeRequirements = {
          minGuestCount: event.minGuestCount,
          maxGuestCount: event.maxGuestCount,
          minMemberCount: event.minMemberCount,
          maxMemberCount: event.maxMemberCount,
          allowedGroupSizes: [...event.allowedGroupSizes],
        };

        initializeEventSignUpProcess({
          steps,
          signUpRow,
          currentMemberUserId: currentMemberUserId ?? null,
          formQuestionConfigurations,
          groupSizeRequirements,
          stepToStartOn,
          onSaveCallback,
          defaultPrimaryChargeId,
          primaryChargeIdToPriceMap: primaryChargeIdToPriceMap ?? [],
          primaryChargeType: event.primaryChargeType ?? null,
          isStaff: isStaff,
          isStaffAdmin: isStaffAdmin,
          eventRegistrationCleanup: eventRegistrationCleanup,
          memberSignUpProxyAllowed: !!event.memberSignUpProxyAllowed,
          eventHasWaitlist: !!event.hasWaitlist,
          isWaitlistSignup,
          eventRemainingCapacity:
            event.capacityType &&
            event.spotsAvailable !== null &&
            event.spotsAvailable !== undefined
              ? {
                  capacityType: event.capacityType,
                  remainingCapacity: event.spotsAvailable,
                }
              : null,
        });
      }
    }
  }, [
    eventHasForm,
    eventRequiresPrimaryChargeWhoPaysSection,
    shouldShowPerRegistrationChargeSelection,
    isGroupEvent,
    signUpRow,
    currentMemberUserId,
    formQuestionConfigurations,
    isOpen,
    onSaveCallback,
    event.minGuestCount,
    event.maxGuestCount,
    event.minMemberCount,
    event.maxMemberCount,
    event.allowedGroupSizes,
    stepToStartOn,
    isMemberUser,
    hasMoreThanOneSelectablePrimaryChargeType,
    initializeEventSignUpProcess,
    defaultPrimaryChargeId,
    primaryChargeIdToPriceMap,
    event.primaryChargeType,
    isStaff,
    isStaffAdmin,
    eventRegistrationCleanup,
    isWaitlistSignup,
    event.memberSignUpProxyAllowed,
    event.hasWaitlist,
    event.capacityType,
    event.spotsAvailable,
    clubSupportsPublic,
  ]);

  return null;
};

const getEventSteps = (
  shouldHavePeopleStep: boolean,
  shouldHaveFormStep: boolean,
  shouldShowPrimaryChargeSelectionStep: boolean,
  shouldShowPrimaryChargeWhoPays: boolean
) => {
  let steps: EventSignUpProcessSteps[] = [];
  if (shouldHavePeopleStep) {
    steps.push("PEOPLE");
  }
  if (shouldHaveFormStep) {
    steps.push("FORM");
  }
  if (shouldShowPrimaryChargeSelectionStep) {
    steps.push("PRIMARY_CHARGE_SELECTION");
  }
  if (shouldShowPrimaryChargeWhoPays) {
    steps.push("PRIMARY_CHARGE_WHO_PAYS");
  }
  return steps;
};

const useInitializeEventSignUpProcessFragmentString = graphql`
  fragment useInitializeEventSignUpProcess_event on Event {
    id
    maxGuestCount
    maxMemberCount
    minGuestCount
    minMemberCount
    allowedGroupSizes
    memberSignUpProxyAllowed
    hasWaitlist
    capacityType
    spotsAvailable
    eventSeries {
      facility {
        club {
          supportsPublic
        }
      }
    }
    formConfiguration {
      id
      formQuestionConfigurations {
        count
        edges {
          node {
            id
            enabled
            guestVisibility
            memberVisibility
            description
            placeholder
            prompt
            shortName
            target
            type
            values
          }
        }
      }
    }
    primaryChargeType
    primaryRegistrationChargeTypes {
      count
    }
    signUpWindows {
      edges {
        node {
          charges {
            edges {
              memberControl
              staffControl
              node {
                id
                chargeRuleSet {
                  id
                  displayName
                  displayDescription
                  chargeRules {
                    edges {
                      node {
                        id
                        duration
                        price
                        billingId
                        description
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    # Member users see only the windows they are eligible for (caller-scoped).
    # Aliased so it never field-merges with other clientEligibleSignUpWindows
    # selections (e.g. the pricing section) that pass a clientId argument.
    eligibleWindowsForCharges: clientEligibleSignUpWindows {
      charges {
        edges {
          memberControl
          staffControl
          node {
            id
            chargeRuleSet {
              id
              displayName
              displayDescription
              chargeRules {
                edges {
                  node {
                    id
                    duration
                    price
                    billingId
                    description
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
